import { Box, Button, Chip, Divider, Link, Paper, Stack, Typography } from "@mui/material";
import { type ReactNode, useState } from "react";
import { useTranslate } from "react-admin";

import { FinanceNotificationRule, type FinanceNotificationsQuery } from "../../generated/graphql";
import { formatCrmCurrency, formatCrmDate, formatCrmDateTime } from "../../lib/crm";
import { endsInFuture } from "../../lib/financeNotifications";
import { color, typography } from "../../theme/designTokens";
import { BillwerkPlanVariantValue } from "./BillwerkPlanVariantValue";
import { DismissFinanceNotificationDialog } from "./DismissFinanceNotificationDialog";

export type FinanceNotificationNode = FinanceNotificationsQuery["financeNotifications"]["nodes"][number];
type Item = NonNullable<FinanceNotificationNode["item"]>;

const NONE = "–";

const text = (value: string | null | undefined): string => value || NONE;

// A block value, such as one holding a chip, needs a div instead of a paragraph.
const Field = ({ label, children, block = false }: { label: string; children: ReactNode; block?: boolean }) => (
  <Box role="group" aria-label={label} sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography component={block ? "div" : "p"} variant="body2" sx={{ overflowWrap: "anywhere" }}>
      {children}
    </Typography>
  </Box>
);

const Fields = ({ children }: { children: ReactNode }) => (
  <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>{children}</Box>
);

const SalesforceLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </Link>
);

const ItemLink = ({ item }: { item: Item }) => (
  <SalesforceLink href={item.salesforceUrl}>{item.name || item.salesforceId}</SalesforceLink>
);

// Where the plan variant comes from: the contract's price book and the product's Billwerk plan.
const PlanVariantField = ({ item }: { item: Item }) => {
  const translate = useTranslate();
  const { planVariant } = item;
  const source = [
    planVariant.priceBookName &&
      translate("financeNotifications.planVariantPriceBook", { name: planVariant.priceBookName }),
    planVariant.billwerkPlanId && translate("financeNotifications.planVariantPlan", { id: planVariant.billwerkPlanId }),
  ].filter(Boolean);
  return (
    <Field label={translate("financeNotifications.fields.planVariant")} block>
      <BillwerkPlanVariantValue planVariant={planVariant} />
      {source.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          {source.join(" · ")}
        </Typography>
      )}
    </Field>
  );
};

const CommonFields = ({ item }: { item: Item }) => {
  const translate = useTranslate();
  const date = (value: string | null | undefined) => (value ? formatCrmDate(value) : NONE);
  return (
    <Fields>
      <Field label={translate("financeNotifications.fields.contractNumber")}>{text(item.contractNumber)}</Field>
      <Field label={translate("financeNotifications.fields.account")}>
        {item.accountName
          ? item.accountRecordType
            ? `${item.accountName} (${item.accountRecordType})`
            : item.accountName
          : NONE}
      </Field>
      <Field label={translate("financeNotifications.fields.product")}>{text(item.productName)}</Field>
      <Field label={translate("financeNotifications.fields.price")}>
        {item.price === null || item.price === undefined ? NONE : formatCrmCurrency(item.price)}
      </Field>
      <Field label={translate("financeNotifications.fields.startDate")}>{date(item.startDate)}</Field>
      <Field label={translate("financeNotifications.fields.endDate")}>{date(item.endDate)}</Field>
      <Field label={translate("financeNotifications.fields.billingStartDate")}>{date(item.billingStartDate)}</Field>
      <Field label={translate("financeNotifications.fields.billingTool")}>{text(item.billingTool)}</Field>
      <PlanVariantField item={item} />
    </Fields>
  );
};

const BillingStopDetails = ({ notification, item }: { notification: FinanceNotificationNode; item: Item }) => {
  const translate = useTranslate();
  const { billwerkEffectiveFrom, returnOrder, noCompletedReturn } = notification;
  return (
    <Stack gap={2}>
      {item.endDate && (
        <Typography variant="body2" fontWeight={typography.fontWeight.semibold}>
          {translate(endsInFuture(item.endDate) ? "financeNotifications.endsOn" : "financeNotifications.endedOn", {
            date: formatCrmDate(item.endDate),
          })}
        </Typography>
      )}
      <Fields>
        <Field label={translate("financeNotifications.fields.billwerkEffectiveFrom")}>
          {billwerkEffectiveFrom ? formatCrmDateTime(billwerkEffectiveFrom) : NONE}
        </Field>
        <Field label={translate("financeNotifications.fields.cancellationType")}>{text(item.cancellationType)}</Field>
        <Field label={translate("financeNotifications.fields.lostReason")}>{text(item.lostReason)}</Field>
        <Field label={translate("financeNotifications.fields.cancellationDate")}>
          {item.cancellationDate ? formatCrmDate(item.cancellationDate) : NONE}
        </Field>
      </Fields>
      {(returnOrder || noCompletedReturn) && (
        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2} data-testid="finance-notification-return">
          {returnOrder && (
            <Fields>
              <Field label={translate("financeNotifications.fields.returnOrder")}>
                {text(returnOrder.orderNumber)}
                {returnOrder.salesforceUrl && (
                  <>
                    {" "}
                    <SalesforceLink href={returnOrder.salesforceUrl}>
                      {translate("financeNotifications.openInSalesforce")}
                    </SalesforceLink>
                  </>
                )}
              </Field>
              <Field label={translate("financeNotifications.fields.returnStatus")}>{text(returnOrder.status)}</Field>
              <Field label={translate("financeNotifications.fields.returnCompletedDate")}>
                {returnOrder.completedDate ? formatCrmDate(returnOrder.completedDate) : NONE}
              </Field>
            </Fields>
          )}
          {noCompletedReturn && (
            <Chip size="small" color="warning" label={translate("financeNotifications.noReturn")} />
          )}
        </Stack>
      )}
    </Stack>
  );
};

const flag = (translate: (key: string) => string, value: boolean | null | undefined): string =>
  value === null || value === undefined
    ? NONE
    : translate(value ? "financeNotifications.yes" : "financeNotifications.no");

const ChangeSide = ({
  title,
  item,
  withPlanVariant = false,
}: {
  title: string;
  item: Item | null | undefined;
  withPlanVariant?: boolean;
}) => {
  const translate = useTranslate();
  return (
    <Stack role="group" aria-label={title} gap={1} sx={{ minWidth: 0 }}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      {item ? (
        <>
          <Typography variant="body2" fontWeight={typography.fontWeight.semibold}>
            <ItemLink item={item} />
          </Typography>
          <Fields>
            <Field label={translate("financeNotifications.fields.status")}>{text(item.status)}</Field>
            <Field label={translate("financeNotifications.fields.startDate")}>
              {item.startDate ? formatCrmDate(item.startDate) : NONE}
            </Field>
            <Field label={translate("financeNotifications.fields.billingStarted")}>
              {flag(translate, item.billingStarted)}
            </Field>
            <Field label={translate("financeNotifications.fields.billingStopped")}>
              {flag(translate, item.billingStopped)}
            </Field>
            {withPlanVariant && <PlanVariantField item={item} />}
          </Fields>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {translate("financeNotifications.itemMissing")}
        </Typography>
      )}
    </Stack>
  );
};

const ContractChangeDetails = ({ notification, item }: { notification: FinanceNotificationNode; item: Item }) => {
  const translate = useTranslate();
  return (
    <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
      <ChangeSide title={translate("financeNotifications.fields.oldItem")} item={item} />
      {/* The old item's plan variant is among the common fields above. */}
      <ChangeSide
        title={translate("financeNotifications.fields.newItem")}
        item={notification.changedInto}
        withPlanVariant
      />
    </Box>
  );
};

const CancellationWithdrawnDetails = ({
  notification,
  item,
}: {
  notification: FinanceNotificationNode;
  item: Item;
}) => {
  const translate = useTranslate();
  const { handledStatus, handledEndDate, handledAt } = notification;
  return (
    <Fields>
      <Field label={translate("financeNotifications.fields.status")}>
        {translate("financeNotifications.change", { before: text(handledStatus), now: text(item.status) })}
      </Field>
      <Field label={translate("financeNotifications.fields.handledEndDate")}>
        {handledEndDate ? formatCrmDate(handledEndDate) : NONE}
      </Field>
      <Field label={translate("financeNotifications.fields.handledAt")}>
        {handledAt ? formatCrmDateTime(handledAt) : NONE}
      </Field>
    </Fields>
  );
};

const EndDateChangedDetails = ({ notification, item }: { notification: FinanceNotificationNode; item: Item }) => {
  const translate = useTranslate();
  const { handledEndDate, handledAt } = notification;
  return (
    <Fields>
      <Field label={translate("financeNotifications.fields.endDate")}>
        {translate("financeNotifications.change", {
          before: handledEndDate ? formatCrmDate(handledEndDate) : NONE,
          now: item.endDate ? formatCrmDate(item.endDate) : NONE,
        })}
      </Field>
      <Field label={translate("financeNotifications.fields.handledAt")}>
        {handledAt ? formatCrmDateTime(handledAt) : NONE}
      </Field>
    </Fields>
  );
};

const RuleDetails = ({ notification, item }: { notification: FinanceNotificationNode; item: Item }) => {
  switch (notification.rule) {
    case FinanceNotificationRule.BillingStopNeeded:
      return <BillingStopDetails notification={notification} item={item} />;
    case FinanceNotificationRule.ContractChangeDue:
      return <ContractChangeDetails notification={notification} item={item} />;
    case FinanceNotificationRule.CancellationWithdrawn:
      return <CancellationWithdrawnDetails notification={notification} item={item} />;
    case FinanceNotificationRule.EndDateChanged:
      return <EndDateChangedDetails notification={notification} item={item} />;
    case FinanceNotificationRule.BillingStartDue:
      return null;
  }
};

// One open notification: the rule, the item it concerns and what Finance needs to act on it.
export const FinanceNotificationCard = ({
  notification,
  canDismiss,
}: {
  notification: FinanceNotificationNode;
  canDismiss: boolean;
}) => {
  const translate = useTranslate();
  const [dismissing, setDismissing] = useState(false);
  const { item } = notification;
  return (
    <Paper
      variant="outlined"
      data-testid="finance-notification"
      sx={{ borderColor: color.panelBorder, p: 2, display: "grid", gap: 2 }}
    >
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5}>
        <Chip size="small" variant="outlined" label={translate(`financeNotifications.rules.${notification.rule}`)} />
        <Typography component="h2" variant="subtitle1" fontWeight={typography.fontWeight.semibold} sx={{ flex: 1 }}>
          {item ? <ItemLink item={item} /> : translate("financeNotifications.itemMissing")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate("financeNotifications.since", { time: formatCrmDateTime(notification.openedAt) })}
        </Typography>
      </Stack>
      {item && (
        <>
          <CommonFields item={item} />
          {notification.rule !== FinanceNotificationRule.BillingStartDue && (
            <>
              <Divider />
              <RuleDetails notification={notification} item={item} />
            </>
          )}
        </>
      )}
      {canDismiss && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" size="small" onClick={() => setDismissing(true)}>
            {translate("financeNotifications.dismiss.action")}
          </Button>
        </Box>
      )}
      {dismissing && (
        <DismissFinanceNotificationDialog notificationId={notification.id} onClose={() => setDismissing(false)} />
      )}
    </Paper>
  );
};
